<template>
    <div>
        <v-card class="py-2 px-4">
            <v-skeleton-loader v-if="loading" class="mx-auto" type="article" />
            <template v-else>
                <v-card-title>
                    {{ $t('message.familyAccounts.title') }}
                </v-card-title>
                <v-card-subtitle>
                    <p>
                        {{ $t('message.familyAccounts.description') }}
                    </p>
                </v-card-subtitle>
                <v-card-text>
                    <div class="tw-flex tw-flex-wrap">
                        <div
                            v-for="user in subAccounts"
                            :key="user.id"
                            class="tw-w-full sm:tw-w-1/2 md:tw-w-1/4 tw-p-2 tw-min-w-[160px]"
                        >
                            <AccountItem
                                :user="user"
                                class="tw-flex-grow"
                                :onEdit="() => onEditAccount(user)"
                                :onUnlink="() => onUnlinkAccount(user)"
                                :onDelete="() => openDeleteDialog(user)"
                            />
                        </div>
                        <!-- Add Account -->
                        <div
                            class="tw-w-full sm:tw-w-1/2 md:tw-w-1/4 tw-p-2 tw-min-w-[160px]"
                        >
                            <v-card class="tw-min-h-[182px] tw-flex">
                                <v-card-text
                                    class="tw-flex-grow tw-flex tw-flex-col tw-justify-center tw-items-center"
                                >
                                    <v-btn
                                        fab
                                        small
                                        color="black"
                                        class="tw-mb-2 tw-shadow-none"
                                        @click="onAddAccount"
                                    >
                                        <v-icon color="white" size="20"
                                            >mdi-plus</v-icon
                                        >
                                    </v-btn>
                                    <span class="tw-font-semibold">{{
                                        $t(
                                            'message.familyAccounts.addAccountButton'
                                        )
                                    }}</span>
                                </v-card-text>
                            </v-card>
                        </div>
                    </div>

                    <ConfigureAccountDialog
                        :key="configureDialog.key"
                        :isOpen="configureDialog.isOpen"
                        :onClose="onCloseConfigureDialog"
                        :action="configureDialog.action"
                        :mainAccountId="mainAccountUser.id"
                        :user="configureDialog.user"
                        :refresh="fetchData"
                    />

                    <UnlinkAccountDialog
                        :key="unlinkDialog.key"
                        :isOpen="unlinkDialog.isOpen"
                        :user="unlinkDialog.user"
                        :onClose="onCloseUnlinkDialog"
                        :mainAccountId="mainAccountUser.id"
                        :refresh="fetchData"
                    />
                    <DeleteSubAccountDialog
                        :key="
                            deleteDialog.user?.id ?? 'delete-sub-account-dialog'
                        "
                        :isOpen="deleteDialog.isOpen"
                        :subAccount="deleteDialog.user"
                        :onClose="onCloseDeleteDialog"
                        :mainAccountName="mainAccountUser.fullName"
                        @onSuccess="fetchData"
                        @onClose="onCloseDeleteDialog"
                        @onClickUnlinkInstead="
                            () => onUnlinkAccount(deleteDialog.user)
                        "
                    />
                </v-card-text>
            </template>
        </v-card>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import AccountItem from './AccountItem.vue'
import ConfigureAccountDialog from './ConfigureAccountDialog.vue'
import UnlinkAccountDialog from './UnlinkAccountDialog.vue'
import { fetchSubAccounts } from '../api/subAccountsApi'
import { useToast } from '@/components/base/toast/composable/useToast'
import DeleteSubAccountDialog from './DeleteSubAccountDialog.vue'

const { showErrorToast } = useToast()

const loading = ref(false)
const { mainAccountUser, isNewFamilyMember } = defineProps({
    mainAccountUser: {
        type: Object,
        required: true,
    },
    isNewFamilyMember: {
        type: Boolean,
        default: false,
    },
})
const subAccounts = ref([])
const configureDialog = ref({
    isOpen: false,
    action: 'add',
    user: {},
    key: 0,
})
const unlinkDialog = ref({
    isOpen: false,
    user: {},
    key: 1,
})

onMounted(async () => {
    await fetchData()
    if (isNewFamilyMember) {
        setTimeout(() => {
            onAddAccount()
        }, 1000)
    }
})

const fetchData = async () => {
    loading.value = true
    const result = await fetchSubAccounts(mainAccountUser.id)
    if (result.success) {
        subAccounts.value = result.data.subAccounts ?? []
    } else {
        showErrorToast(result.error)
    }
    loading.value = false
}

const openConfigureDialog = (action, user) => {
    configureDialog.value.user = user
    configureDialog.value.action = action
    configureDialog.value.isOpen = true
    configureDialog.value.key += 2
}

const onCloseConfigureDialog = () => {
    configureDialog.value.isOpen = false
    configureDialog.value.user = null
    configureDialog.value.key -= 2
}

const onAddAccount = () => {
    openConfigureDialog('add', null)
}

const onEditAccount = (user) => {
    openConfigureDialog('edit', user)
}

const onUnlinkAccount = (user) => {
    unlinkDialog.value.user = user
    unlinkDialog.value.isOpen = true
    unlinkDialog.value.key += 2
}

const onCloseUnlinkDialog = () => {
    unlinkDialog.value.isOpen = false
    unlinkDialog.value.key -= 2
}
const deleteDialog = ref({
    isOpen: false,
    user: {},
})
const openDeleteDialog = (user) => {
    deleteDialog.value.isOpen = true
    deleteDialog.value.user = user
}
const onCloseDeleteDialog = () => {
    deleteDialog.value.isOpen = false
    deleteDialog.value.user = {}
}
</script>
